// Login único pelo portal Acesso CCB (contrato claims-acesso v1.1.0, §9).
// O protocolo (state, PKCE, troca do código e signInWithCustomToken) fica no cliente de referência
// `src/sso/acesso-sso.ts`, copiado do portal sem alteração. Aqui só a cola do app: para onde voltar
// depois do login e como cada erro aparece na tela. Sem import em tempo de execução, para os testes
// rodarem com `node --test` (o App liga as funções do cliente).
import type { CodigoErroSso, OpcoesSso } from "./sso/acesso-sso";

export const SSO_CALLBACK_PATH = "/sso/callback";
const DESTINO_KEY = "docs:sso-destino";

/** `redirect_uri` do app: a origem atual + `/sso/callback` (catálogo do portal, comparação exata). */
export function redirectUriSso(origem: string): string {
  return `${origem}${SSO_CALLBACK_PATH}`;
}

/** Portal e functions: padrão de produção; `VITE_SSO_PORTAL`/`VITE_SSO_FUNCOES` só para os emuladores do portal. */
export function opcoesSso(env: Record<string, string | undefined> | undefined): OpcoesSso {
  return { portal: env?.VITE_SSO_PORTAL || undefined, funcoes: env?.VITE_SSO_FUNCOES || undefined };
}

/** Só caminhos internos do próprio app; o retorno do SSO nunca é destino. */
export function destinoSeguro(destino: string | null | undefined): string {
  if (!destino || !destino.startsWith("/") || destino.startsWith("//") || destino.startsWith("/\\")) return "/";
  return destino.split(/[?#]/)[0] === SSO_CALLBACK_PATH ? "/" : destino;
}

export type Iniciar = (app: string, redirectUri: string) => Promise<string>;

/** "Entrar com a conta CCB": guarda a página pedida e leva ao portal. */
export async function entrarComContaCcb(iniciar: Iniciar, appId: string, origem: string, destino: string, armazenamento: Storage): Promise<string> {
  armazenamento.setItem(DESTINO_KEY, destinoSeguro(destino));
  return iniciar(appId, redirectUriSso(origem));
}

export type AvisoSso = { tipo: "erro"; titulo: string; texto: string };
export type RetornoSso =
  | { tipo: "ok"; destino: string }
  | { tipo: "sem-acesso"; destino: string }
  | { tipo: "erro"; destino: string; codigo: CodigoErroSso | "desconhecido"; aviso: AvisoSso };

const CODIGOS: readonly string[] = ["estado-invalido", "sem-acesso", "codigo-invalido", "conta-desativada", "muitas-tentativas", "erro-portal", "resposta-invalida", "rede"];
const codigoSso = (erro: unknown): CodigoErroSso | null => {
  const codigo = typeof erro === "object" && erro !== null && (erro as { name?: unknown }).name === "ErroSso" ? (erro as { codigo?: unknown }).codigo : null;
  return typeof codigo === "string" && CODIGOS.includes(codigo) ? (codigo as CodigoErroSso) : null;
};
const codigoFirebase = (erro: unknown) => (typeof erro === "object" && erro !== null && "code" in erro ? String((erro as { code: unknown }).code) : "");

/** Aviso do login para um erro da volta do portal (textos do protótipo aprovado e do `ErroSso`). */
export function avisoDoErro(erro: unknown): { codigo: CodigoErroSso | "desconhecido"; aviso: AvisoSso } {
  const codigo = codigoSso(erro);
  if (codigo === "codigo-invalido") {
    return { codigo, aviso: { tipo: "erro", titulo: "Não foi possível concluir o login.", texto: "O código expirou ou já foi usado. Tente entrar de novo." } };
  }
  if (codigo) return { codigo, aviso: { tipo: "erro", titulo: "Não foi possível entrar.", texto: (erro as Error).message } };
  if (codigoFirebase(erro) === "auth/network-request-failed") {
    return { codigo: "rede", aviso: { tipo: "erro", titulo: "Não foi possível entrar.", texto: "Sem conexão. Verifique a internet e tente de novo." } };
  }
  return { codigo: "desconhecido", aviso: { tipo: "erro", titulo: "Não foi possível entrar.", texto: "Não foi possível concluir o login. Tente entrar de novo." } };
}

/**
 * Conclui o login na volta do portal (`/sso/callback`). `concluir` é o `concluirLoginSso` do cliente
 * de referência já ligado à URL e ao Auth do app. `access_denied` (ou a troca recusada por falta
 * de papel) vira `sem-acesso`; os demais erros voltam ao login com o aviso.
 */
export async function concluirRetornoSso(concluir: () => Promise<unknown>, armazenamento: Storage): Promise<RetornoSso> {
  const destino = destinoSeguro(armazenamento.getItem(DESTINO_KEY));
  armazenamento.removeItem(DESTINO_KEY);
  try {
    await concluir();
    return { tipo: "ok", destino };
  } catch (erro) {
    if (codigoSso(erro) === "sem-acesso") return { tipo: "sem-acesso", destino };
    return { tipo: "erro", destino, ...avisoDoErro(erro) };
  }
}
